import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import Logo from '../common/Logo';
import UserDropdownMenu from '../common/UserDropdownMenu';
import styles from '../../styles/layout/Sidebar.module.scss';

interface SidebarProps {
  isCollapsed: boolean;
  isMobileOpen: boolean;
  onToggleCollapse: () => void;
  onToggleMobile: () => void;
  onCloseMobile: () => void;
}

interface NavigationItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  exactMatch?: boolean;
}

const navigationItems: NavigationItem[] = [
  // {
  //   path: '/',
  //   label: 'Dashboard',
  //   icon: (
  //     <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
  //       <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
  //       <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
  //       <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
  //       <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
  //     </svg>
  //   ),
  //   exactMatch: true
  // },
  {
    path: '/credit-lines',
    label: 'Credit Lines',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/>
      </svg>
    )
  },
  {
    path: '/credit-fee-payments',
    label: 'Credit Fee Payments',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
        <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/>
        <path d="M7 14l3-3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    path: '/transactions',
    label: 'Transactions',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    )
  },
  {
    path: '/whitelist',
    label: 'Whitelist',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    )
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
      </svg>
    )
  }
];

function Sidebar({ isCollapsed, isMobileOpen, onToggleCollapse, onToggleMobile, onCloseMobile }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string, exactMatch?: boolean) => {
    if (exactMatch) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    // Close mobile sidebar after navigation
    onCloseMobile();
  };

  return (
    <aside className={clsx(
      styles.sidebar,
      'enterprise-sidebar',
      {
        [styles.collapsed]: isCollapsed,
        [styles.mobileOpen]: isMobileOpen
      }
    )}>
      <div className={styles.sidebarContent}>
        {/* Logo Section */}
        <div className={styles.logoSection}>
          <Logo displayBrandmark={isCollapsed} />
          {!isCollapsed && (
            <button
              className={styles.collapseButton}
              onClick={onToggleCollapse}
              aria-label="Collapse sidebar"
            >
              ←
            </button>
          )}
        </div>

        {/* Navigation Section */}
        <nav className={styles.navigation}>
          <ul className={styles.navigationList}>
            {navigationItems.map((item) => (
              <li key={item.path} className={styles.navigationItem}>
                <button
                  className={clsx(
                    styles.navigationLink,
                    {
                      [styles.active]: isActive(item.path, item.exactMatch)
                    }
                  )}
                  onClick={() => handleNavigate(item.path)}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className={styles.navigationIcon}>{item.icon}</span>
                  {!isCollapsed && (
                    <span className={styles.navigationLabel}>{item.label}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Section */}
        <div className={styles.userSection}>
          <UserDropdownMenu isCollapsed={isCollapsed} />
        </div>
      </div>

      {/* Expand button for collapsed state */}
      {isCollapsed && (
        <button
          className={styles.expandButton}
          onClick={onToggleCollapse}
          aria-label="Expand sidebar"
        >
          →
        </button>
      )}
    </aside>
  );
}

export default Sidebar;
